export const SOURCE_RELEVANCE_MIN = 0.7;

// OpenAI vector-store scores are not on the lexical scale. Measured against real
// workspace data: a clearly on-topic PDF lands around 0.25-0.35 for a Polish
// query (0.85+ for an English one), while an unrelated document sits near 0.05.
// Judging those at 0.70 would bury every workspace document.
export const VECTOR_STORE_RELEVANCE_MIN = 0.22;

const thresholdForSource = (source, minScore) => (
  source?.scoreScale === 'vector_store' ? VECTOR_STORE_RELEVANCE_MIN : minScore
);

const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'your', 'into', 'over',
  'article', 'guide', 'tips', 'best', 'how', 'why', 'what', 'when', 'about',
  'are', 'was', 'were', 'been', 'being', 'have', 'has', 'had', 'not', 'but',
  'you', 'they', 'their', 'them', 'our', 'its', 'can', 'may', 'will', 'than',
  'oraz', 'dla', 'lub', 'albo', 'or', 'to', 'of', 'in', 'on', 'a', 'an', 'at',
  'by', 'is', 'as', 'be', 'we', 'it'
]);

const SYNONYMS = {
  poland: ['polish'],
  polish: ['poland'],
  children: ['child', 'pediatric', 'paediatric'],
  child: ['children', 'pediatric'],
  therapy: ['treatment', 'therapeutic'],
  treatment: ['therapy', 'therapeutic'],
  court: ['courts', 'judicial', 'legal'],
  courts: ['court', 'judicial'],
  law: ['legal', 'legislation'],
  legal: ['law', 'legislation'],
  divorce: ['divorces', 'divorced'],
  cancer: ['oncology', 'tumor', 'tumour'],
  diabetes: ['diabetic', 'glycemic']
};

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stemToken(token) {
  if (token.length < 5) return token;
  return token.replace(/(tions|tion|ments|ment|ings|ing|ies|ied|ers|er|ed|es|s)$/i, '');
}

function tokenize(value) {
  return normalizeText(value)
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length >= 3 && !STOPWORDS.has(token))
    .map(stemToken);
}

function expandToken(token) {
  const aliases = SYNONYMS[token] || [];
  return [token, ...aliases.map(stemToken)];
}

function tokenMatches(haystackSet, token) {
  const variants = expandToken(token);
  for (const variant of variants) {
    if (haystackSet.has(variant)) return true;
    if (variant.length < 5) continue;
    for (const candidate of haystackSet) {
      if (candidate.length < 5) continue;
      if (candidate.startsWith(variant) || variant.startsWith(candidate)) return true;
    }
  }
  return false;
}

function uniqueTokens(values, limit = 16) {
  const seen = new Set();
  const tokens = [];
  values.flatMap((value) => tokenize(value)).forEach((token) => {
    if (seen.has(token)) return;
    seen.add(token);
    tokens.push(token);
  });
  return tokens.slice(0, limit);
}

function coverage(topicTokens, haystackSet) {
  if (!topicTokens.length) return 0;
  let hits = 0;
  topicTokens.forEach((token) => {
    if (tokenMatches(haystackSet, token)) hits += 1;
  });
  return hits / topicTokens.length;
}

function precision(topicTokens, sourceTokens) {
  if (!sourceTokens.length) return 0;
  let hits = 0;
  sourceTokens.forEach((token) => {
    if (tokenMatches(new Set(topicTokens), token)) hits += 1;
  });
  return hits / sourceTokens.length;
}

function bigramHitRate(topicText, sourceText) {
  const topic = tokenize(topicText);
  const source = new Set(tokenize(sourceText));
  if (topic.length < 2 || source.size === 0) return 0;
  let hits = 0;
  let total = 0;
  for (let i = 0; i < topic.length - 1; i += 1) {
    const left = topic[i];
    const right = topic[i + 1];
    total += 1;
    if (source.has(left) && source.has(right)) hits += 1;
  }
  return total ? hits / total : 0;
}

function calibrateScore(lexical) {
  const clamped = Math.max(0, Math.min(1, lexical));
  // Stretch mid-range so on-topic papers land around 0.75-0.95
  // and off-topic papers stay clearly below 0.70.
  const sigmoid = 1 / (1 + Math.exp(-9 * (clamped - 0.28)));
  return Number(Math.max(0, Math.min(1, sigmoid)).toFixed(2));
}

function compactTopicText(topicText) {
  return String(topicText || '').replace(/\s+/g, ' ').trim().slice(0, 220);
}

export function scoreSourceRelevance(source, topicText, searchQueries = []) {
  const title = String(source?.title || '');
  const abstract = `${source?.abstract || ''} ${source?.summary || ''}`.replace(/abstract not available/gi, '');
  const compactTopic = compactTopicText(topicText);
  const queryHints = [
    ...(Array.isArray(searchQueries) ? searchQueries : []),
    source?.query,
    compactTopic
  ].filter(Boolean);

  const topicTokens = uniqueTokens(queryHints, 10);
  const titleTokens = tokenize(title);
  const abstractTokens = tokenize(abstract);
  const titleSet = new Set(titleTokens);
  const abstractSet = new Set(abstractTokens);

  const titleCoverage = coverage(topicTokens, titleSet);
  const titlePrecision = precision(topicTokens, titleTokens);
  const abstractCoverage = coverage(topicTokens, abstractSet);
  const phrases = bigramHitRate(queryHints.join(' '), `${title} ${abstract}`);

  const lexical = (titleCoverage * 0.50)
    + (titlePrecision * 0.22)
    + (abstractCoverage * 0.18)
    + (phrases * 0.10);

  return calibrateScore(lexical);
}

export function rankAndFilterSources(sources = [], topicText = '', searchQueries = [], minScore = SOURCE_RELEVANCE_MIN) {
  const scored = (Array.isArray(sources) ? sources : []).map((source) => {
    // Providers that return their own relevance (vector store search) keep it —
    // the lexical scorer has no abstract to work with for a PDF chunk.
    const relevanceScore = Number.isFinite(source.relevanceScore)
      ? Number(source.relevanceScore)
      : scoreSourceRelevance(source, topicText, searchQueries);
    const threshold = thresholdForSource(source, minScore);

    return {
      ...source,
      relevanceScore,
      threshold,
      belowThreshold: relevanceScore < threshold,
      // Scores from different providers are not comparable, so rank by how far
      // each clears its own bar. Keeps a strong workspace PDF above a barely
      // passing paper instead of sorting it to the bottom on raw score.
      matchStrength: threshold > 0 ? relevanceScore / threshold : relevanceScore
    };
  });

  const ranked = scored.sort((a, b) => b.matchStrength - a.matchStrength);
  const kept = ranked.filter((source) => !source.belowThreshold);

  return {
    kept,
    // Everything, scored and sorted — callers that show a table render this so a
    // weak match is visible and judgeable instead of silently dropped.
    all: ranked,
    rejected: ranked.length - kept.length,
    minScore
  };
}

export function formatRelevanceScore(score) {
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return '0.00';
  return numeric.toFixed(2);
}
